import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { forgotPassword } from '@/api/auth'
import type { ApiError } from '@/api/client'

/** Request a password-reset link. The backend always answers 204, so we show the same
 *  "check your email" confirmation whether or not the address has an account. */
export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [slug, setSlug] = useState('')
  const send = useMutation({
    mutationFn: ({ email, slug }: { email: string; slug: string }) => forgotPassword(email, slug),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (email.trim()) send.mutate({ email: email.trim(), slug: slug.trim() })
  }

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-sm">
        <h1 className="text-lg font-semibold text-brand-darkest mb-2">Reset your password</h1>
        {send.isSuccess ? (
          <>
            <p className="text-sm text-gray-500 mb-6">
              If an account exists for <span className="font-medium">{email.trim()}</span>, we've sent a
              link to reset your password. Check your inbox — the link expires in 1 hour.
            </p>
            <Link
              to="/login"
              className="inline-block w-full text-center bg-brand-primary text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors"
            >
              Back to sign in
            </Link>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-6">
              Enter your email and we'll send you a link to set a new password.
            </p>
            <form onSubmit={onSubmit}>
              <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
              <input
                type="email"
                value={email}
                placeholder="you@agency.com"
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mb-4 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
              />
              <label className="block text-xs font-medium text-gray-500 mb-1">Workspace <span className="text-gray-400">(optional)</span></label>
              <input
                type="text"
                value={slug}
                placeholder="your-agency"
                autoComplete="off"
                onChange={(e) => setSlug(e.target.value)}
                className="w-full mb-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
              />
              <p className="text-xs text-gray-400 mb-4">If your email is used in more than one workspace, enter it to reset just that one.</p>
              {send.isError && (
                <p className="text-danger text-xs mb-4">{(send.error as ApiError)?.message ?? 'Something went wrong — please try again.'}</p>
              )}
              <button
                type="submit"
                disabled={send.isPending || !email.trim()}
                className="w-full bg-brand-primary text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors disabled:opacity-60"
              >
                {send.isPending ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
            <p className="text-center text-xs text-gray-400 mt-6">
              <Link to="/login" className="text-brand-primary hover:underline">
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  )
}
